import { type FC, useEffect, useState } from 'react'

import { AvatarImages } from '@liveHelper/components/Idle/AvatarImages'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { useCloseWidgetContext } from '@liveHelper/context/CloseWidgetContext'
import { useIsOffline } from '@liveHelper/hooks/useIsOffline'
import {
  actionsSelector,
  conversationSelector,
  useStore,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_BASE_CONTAINER } from '@liveHelper/testIds'
import { useTrackClickWidget } from '@liveHelper/tracking/useLiveHelperTracking'

import { Close } from '@core/assets/Close'
import { useContextTranslation } from '@core/i18n'

import { CloseWidgetButton, GreenDot, MultiAgentContainer } from './Idle.styles'

/**
 * Folded avatars state of the idle widget.
 */
export const IdleFoldedAvatars: FC = () => {
  const { t } = useContextTranslation()

  const conversation = useStore(conversationSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const { lastUnreadMessage } = useMessagePreviewContext()
  const { canCloseWidget, closeWidget } = useCloseWidgetContext()
  const isOffline = useIsOffline()
  const trackClickWidget = useTrackClickWidget()

  const [isHiding, setIsHiding] = useState(false)

  useEffect(() => {
    if (isHiding) {
      const timeoutId = setTimeout(() => {
        setIsHiding(false)

        conversation
          ? sendUIStateTransitionEvent('START_CHAT')
          : sendUIStateTransitionEvent('EXPAND')
      }, 400)

      return () => clearTimeout(timeoutId)
    }
  }, [conversation, isHiding, sendUIStateTransitionEvent])

  function handleExpandWidget() {
    trackClickWidget({
      click_target: 'minimized_widget',
      last_unread_message: lastUnreadMessage?.message,
    })

    setIsHiding(true)
  }

  const handleCloseWidget = (e) => {
    e.stopPropagation()
    closeWidget()
  }

  return (
    <MultiAgentContainer
      tabIndex={0}
      aria-label={t('oneToOne:One-to-one chat')}
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          handleExpandWidget()
        }
      }}
    >
      {canCloseWidget && (
        <CloseWidgetButton
          isAvatarUI
          aria-label={t(`oneToOne:Close widget`)}
          actionType="neutral"
          buttonSize="xs"
          shape="circle"
          onClick={handleCloseWidget}
          icon={<Close height="8px" width="8px" label="" aria-hidden />}
        />
      )}
      <AvatarImages isHiding={isHiding} onClick={handleExpandWidget}>
        {!isOffline ? <GreenDot isHiding={isHiding} /> : null}
      </AvatarImages>
    </MultiAgentContainer>
  )
}
